import React, { useEffect, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  IconButton,
  Collapse,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Checkbox,
  FormGroup,
  FormControlLabel,
} from '@mui/material';
import {
  Search,
  FilterList,
  Add,
  ExpandMore,
  ExpandLess,
  Edit,
  Delete,
  ContentCopy,
  Visibility,
} from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { RootState } from '../store';
import { fetchItems, setFilters, setPage, setPageSize, toggleExpandedRow } from '../store/slices/itemsSlice';
import { ItemFilters, Subject, Grade, BloomLevel } from '../types/item';

const ItemListPage: React.FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const {
    items,
    totalCount,
    currentPage,
    pageSize,
    totalPages,
    filters,
    isLoading,
    expandedRows,
  } = useSelector((state: RootState) => state.items);

  const [localFilters, setLocalFilters] = useState<ItemFilters>(filters);
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    dispatch(fetchItems({
      filters,
      pagination: { page: currentPage, pageSize }
    }) as any);
  }, [dispatch, filters, currentPage, pageSize]);

  const handleSearch = () => {
    dispatch(setFilters(localFilters));
  };

  const handleClearFilters = () => {
    const emptyFilters: ItemFilters = {};
    setLocalFilters(emptyFilters);
    dispatch(setFilters(emptyFilters));
  };

  const handlePageChange = (event: unknown, newPage: number) => {
    dispatch(setPage(newPage + 1)); // MUI uses 0-based, we use 1-based
  };

  const handlePageSizeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    dispatch(setPageSize(parseInt(event.target.value, 10)));
  };

  const handleRowExpand = (itemId: string) => {
    dispatch(toggleExpandedRow(itemId));
  };

  const handleEditItem = (itemId: string) => {
    navigate(`/items/${itemId}/edit`);
  };

  const handleNewItem = () => {
    navigate('/items/new');
  };

  const isRowExpanded = (itemId: string) => expandedRows.includes(itemId);

  const formatGrades = (grades: Grade[]) => {
    return [...grades].sort().join(', ');
  };

  const formatTags = (tags: string[]) => {
    return tags.slice(0, 3).map(tag => (
      <Chip key={tag} label={tag} size="small" sx={{ mr: 0.5, mb: 0.5 }} />
    ));
  };

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <Box sx={{ p: 3, pb: 0 }}>
        <Typography variant="h4" gutterBottom sx={{ fontWeight: 600 }}>
          Content Items
        </Typography>
      </Box>

      {/* Search and Filter Bar */}
      <Paper sx={{ m: 3, p: 2 }}>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 2 }}>
          <TextField
            placeholder="Search items..."
            variant="outlined"
            size="small"
            value={localFilters.search || ''}
            onChange={(e) => setLocalFilters({ ...localFilters, search: e.target.value })}
            InputProps={{
              startAdornment: <Search sx={{ color: 'text.secondary', mr: 1 }} />,
            }}
            sx={{ minWidth: 300 }}
          />
          
          <Button
            variant="contained"
            onClick={handleSearch}
            startIcon={<Search />}
          >
            Search
          </Button>
          
          <Button
            variant="outlined"
            onClick={() => setShowFilters(!showFilters)}
            startIcon={<FilterList />}
          >
            Filters
          </Button>
          
          <Box sx={{ flexGrow: 1 }} />
          
          <Button
            variant="contained"
            color="primary"
            onClick={handleNewItem}
            startIcon={<Add />}
          >
            New Item
          </Button>
        </Box>

        {/* Advanced Filters */}
        <Collapse in={showFilters}>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', pt: 2, borderTop: 1, borderColor: 'divider' }}>
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Subject</InputLabel>
              <Select
                value={localFilters.subject || ''}
                onChange={(e) => setLocalFilters({ ...localFilters, subject: e.target.value as Subject })}
                label="Subject"
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="Math">Math</MenuItem>
                <MenuItem value="Science">Science</MenuItem>
                <MenuItem value="English">English</MenuItem>
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Bloom Level</InputLabel>
              <Select
                value={localFilters.bloomLevel || ''}
                onChange={(e) => setLocalFilters({ ...localFilters, bloomLevel: e.target.value as BloomLevel })}
                label="Bloom Level"
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="Remember">Remember</MenuItem>
                <MenuItem value="Understand">Understand</MenuItem>
                <MenuItem value="Apply">Apply</MenuItem>
                <MenuItem value="Analyze">Analyze</MenuItem>
                <MenuItem value="Evaluate">Evaluate</MenuItem>
                <MenuItem value="Create">Create</MenuItem>
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Difficulty</InputLabel>
              <Select
                value={localFilters.difficulty || ''}
                onChange={(e) => setLocalFilters({ ...localFilters, difficulty: Number(e.target.value) as any })}
                label="Difficulty"
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value={1}>⭐ Easy</MenuItem>
                <MenuItem value={2}>⭐⭐ Medium</MenuItem>
                <MenuItem value={3}>⭐⭐⭐ Hard</MenuItem>
                <MenuItem value={4}>⭐⭐⭐⭐ Very Hard</MenuItem>
                <MenuItem value={5}>⭐⭐⭐⭐⭐ Expert</MenuItem>
              </Select>
            </FormControl>

            <Button variant="outlined" onClick={handleClearFilters}>
              Clear Filters
            </Button>
          </Box>
        </Collapse>
      </Paper>

      {/* Table */}
      <Paper sx={{ mx: 3, mb: 3, flex: 1, display: 'flex', flexDirection: 'column' }}>
        <TableContainer sx={{ flex: 1 }}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell width={50}></TableCell>
                <TableCell>ID</TableCell>
                <TableCell>Question Preview</TableCell>
                <TableCell>Subject</TableCell>
                <TableCell>Grades</TableCell>
                <TableCell>Bloom Level</TableCell>
                <TableCell>Difficulty</TableCell>
                <TableCell>Tags</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Updated</TableCell>
                <TableCell width={120}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {items.map((item) => (
                <React.Fragment key={item.id}>
                  <TableRow
                    className="item-table-row"
                    sx={{ 
                      backgroundColor: isRowExpanded(item.id) ? 'rgba(25, 118, 210, 0.08)' : 'inherit'
                    }}
                  >
                    <TableCell>
                      <IconButton
                        size="small"
                        onClick={() => handleRowExpand(item.id)}
                      >
                        {isRowExpanded(item.id) ? <ExpandLess /> : <ExpandMore />}
                      </IconButton>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                        {item.id.substring(0, 8)}...
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ maxWidth: 300 }}>
                        {item.questionPreview}
                      </Typography>
                    </TableCell>
                    <TableCell>{item.subject}</TableCell>
                    <TableCell>{formatGrades(item.grades)}</TableCell>
                    <TableCell>{item.bloomLevel}</TableCell>
                    <TableCell>
                      {'⭐'.repeat(item.difficulty)}
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap' }}>
                        {formatTags(item.tags)}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={item.status}
                        size="small"
                        color={item.status === 'published' ? 'success' : 'default'}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {new Date(item.updatedAt).toLocaleDateString()}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        <IconButton size="small" onClick={() => handleEditItem(item.id)}>
                          <Edit fontSize="small" />
                        </IconButton>
                        <IconButton size="small">
                          <Visibility fontSize="small" />
                        </IconButton>
                        <IconButton size="small">
                          <ContentCopy fontSize="small" />
                        </IconButton>
                        <IconButton size="small" color="error">
                          <Delete fontSize="small" />
                        </IconButton>
                      </Box>
                    </TableCell>
                  </TableRow>
                  
                  {/* Expanded Row Content */}
                  <TableRow>
                    <TableCell colSpan={11} sx={{ py: 0 }}>
                      <Collapse in={isRowExpanded(item.id)} timeout="auto" unmountOnExit>
                        <Box sx={{ p: 2, bgcolor: 'grey.50' }}>
                          <Typography variant="subtitle2" gutterBottom>
                            Full Question:
                          </Typography>
                          <Typography variant="body2" sx={{ mb: 2 }}>
                            {/* This would show the full question with rich content */}
                            {item.questionPreview}...
                          </Typography>
                          
                          <Typography variant="subtitle2" gutterBottom>
                            Metadata:
                          </Typography>
                          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                            <Typography variant="body2">
                              <strong>Created:</strong> {new Date(item.createdAt).toLocaleDateString()}
                            </Typography>
                            <Typography variant="body2">
                              <strong>Author:</strong> {item.createdBy}
                            </Typography>
                            <Typography variant="body2">
                              <strong>All Tags:</strong> {item.tags.join(', ')}
                            </Typography>
                          </Box>
                        </Box>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Pagination */}
        <TablePagination
          component="div"
          count={totalCount}
          page={currentPage - 1} // MUI uses 0-based, we use 1-based
          onPageChange={handlePageChange}
          rowsPerPage={pageSize}
          onRowsPerPageChange={handlePageSizeChange}
          rowsPerPageOptions={[10, 20, 30, 50, 100]}
        />
      </Paper>
    </Box>
  );
};

export default ItemListPage;
